import {
  ComponentBaseProps,
  FADButton,
  StackedList,
  DialogPosition,
  UserAlertListDataItem,
  ActionModal,
  AlertSeverityType,
  SearchDropDown,
  SearchDropDownOption,
  LoadingSpinner,
} from '@ecdlink/ui';
import { intervalToDuration } from 'date-fns';
import {
  useDialog,
  getAvatarColor,
  MotherDto,
  InfantDto,
  getStringFromClassNameOrId,
} from '@ecdlink/core';
import { IconInformationIndicator } from '@/components/icon-information-indicator/icon-information-indicator';
import * as styles from './client-list.styles';
import { useSelector } from 'react-redux';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import ROUTES from '@/routes/routes';
import { useHistory, useLocation, generatePath } from 'react-router-dom';
import { getInfants } from '@/store/infant/infant.selectors';
import { motherSelectors, motherThunkActions } from '@/store/mother';
import PregnantIcon from '@/assets/svg/pregnant';
import InfantIcon from '@/assets/svg/infant';
import { ReactComponent as BinocularsIcon } from '@/assets/binocularsIcon.svg';
import { useAppDispatch } from '@/store';
import SearchHeader from '@/components/search-header/search-header';
import { infantThunkActions } from '@/store/infant';
import {
  ageOptions,
  ClientTabIndex,
  clientTypeOptions,
  ExtraInfantData,
  ExtraMotherData,
  filterByAge,
  filterByClientType,
  filterBySort,
  searchList,
  SortBy,
  sortOptions,
} from './filters';
import { ClientDashboardRouteState } from '../client-dashboard/class-dashboard.types';
import { useThunkFetchCall } from '@/hooks/useThunkFetchCall';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { MotherActions } from '@/store/mother/mother.actions';
import { InfantActions } from '@/store/infant/infant.actions';
import { clientSteps } from './walkthrough/steps';
import { multipleClientsSteps } from './walkthrough/steps_multiple_clients';
import { DUMMY_CLIENTS } from './walkthrough/clients-dummy';
import { useWalkthroughContext } from '@/context/walkthrougContext';
import { RootState } from '@/store/types';

export const useClientProfileDialog = () => {
  const history = useHistory();

  const navigate = useCallback(
    (
      activeTabIndex: number,
      client: MotherDto | InfantDto,
      clientType: 'mother' | 'infant',
      onClose: () => void
    ) => {
      history.push(
        `${
          clientType === 'mother'
            ? ROUTES.CLIENTS.MOM_PROFILE.ROOT
            : ROUTES.CLIENTS.INFANT_PROFILE.ROOT
        }${client.user?.id ?? client.userId ?? client.id}`,
        {
          activeTabIndex,
        }
      );
      onClose();
    },
    [history]
  );

  const dialog = useDialog();

  const profileDialog = ({
    client,
    clientType,
  }: {
    client: MotherDto | InfantDto;
    clientType: 'infant' | 'mother';
  }) => {
    const NAVIGATE = ClientTabIndex;

    return dialog({
      position: DialogPosition.Middle,
      color: 'bg-white',
      render(onClose) {
        return (
          <ActionModal
            className={'mx-4'}
            title={`What do you want to do on ${client.user?.firstName}’s profile?`}
            actionButtons={[
              {
                text: 'Visit client',
                colour: 'primary',
                onClick: () =>
                  navigate(NAVIGATE.VISITS, client, clientType, onClose),
                type: 'filled',
                textColour: 'white',
                leadingIcon: 'HomeIcon',
              },
              {
                text: 'See client’s progress',
                colour: 'primary',
                onClick: () =>
                  navigate(NAVIGATE.PROGRESS, client, clientType, onClose),
                type: 'outlined',
                textColour: 'primary',
                leadingIcon: 'PresentationChartLineIcon',
              },
              {
                text: 'See referrals',
                colour: 'primary',
                onClick: () =>
                  navigate(NAVIGATE.REFERRALS, client, clientType, onClose),
                type: 'outlined',
                textColour: 'primary',
                leadingIcon: 'ClipboardListIcon',
              },
              {
                text: 'Contact client',
                colour: 'primary',
                onClick: () =>
                  navigate(NAVIGATE.CONTACT, client, clientType, onClose),
                type: 'outlined',
                textColour: 'primary',
                leadingIcon: 'PhoneIcon',
              },
              {
                text: 'Something else',
                colour: 'primary',
                onClick: () =>
                  navigate(NAVIGATE.VISITS, client, clientType, onClose),
                type: 'outlined',
                textColour: 'primary',
              },
            ]}
          />
        );
      },
    });
  };

  return profileDialog;
};

export const ClientList: React.FC<ComponentBaseProps> = () => {
  const { isLoading: isLoadingMothers } = useThunkFetchCall(
    'mothers',
    MotherActions.GET_MOTHERS
  );
  const { isLoading: isLoadingInfants } = useThunkFetchCall(
    'infants',
    InfantActions.GET_INFANTS
  );
  const [isLoadingMotherStatusInfo, setIsLoadingMotherStatusInfo] =
    useState(false);
  const [isLoadingInfantStatusInfo, setIsLoadingInfantStatusInfo] =
    useState(false);

  const { setState, state } = useWalkthroughContext();

  const { isOnline } = useOnlineStatus();

  const dialog = useDialog();

  const appDispatch = useAppDispatch();
  const profileDialog = useClientProfileDialog();

  const history = useHistory();
  const location = useLocation<ClientDashboardRouteState>();

  const infants = useSelector(getInfants);
  const mothers = useSelector(motherSelectors.getMothers);
  const allMothersRaw = useSelector(
    (state: RootState) => state.mothers.mothers
  );

  // Walkthrough always uses multi-client script + dummy list (not real caseload).
  const isMultiClientsTour = state.tourActive;

  const advanceWalkthroughStep = useCallback(
    (nextStepIndex: number) => {
      if (!state.tourActive) return;
      setState({ run: true, tourActive: true, stepIndex: nextStepIndex });
    },
    [setState, state.tourActive]
  );

  /** Stable dummy rows for the clients walkthrough (never real user data). */
  const walkthroughListItems = useMemo((): UserAlertListDataItem[] => {
    return DUMMY_CLIENTS.map((client) => ({
      id: client.id,
      title: client.title,
      subTitle: client.subTitle,
      switchTextStyles: true,
      alertSeverity: client.alertSeverity,
      alertSeverityNoneIcon:
        client.subTitle === 'New client' ? 'CheckCircleIcon' : 'CalendarIcon',
      alertSeverityNoneColor:
        client.subTitle === 'New client' ? 'successDark' : 'black',
      avatarColor: getAvatarColor('growgreat') || '',
      svgIcon: client.kind === 'mother' ? PregnantIcon : InfantIcon,
      noClick: true,
      extraData: {
        kind: client.kind,
        under6Months: false,
      },
    }));
  }, []);

  // use this to show multiple children view
  const infantsByCaregiver = useSelector((state: RootState) =>
    infants.reduce((map, inf) => {
      const cid = inf.caregiver?.id;
      if (!cid) return map;
      if (!map[cid]) map[cid] = [];
      map[cid].push(inf);
      return map;
    }, {} as Record<string, InfantDto[]>)
  );

  // get infant ids without visit data to fetch visits and their answers
  const infantsWithoutVisits =
    infants
      .filter((x) => {
        if (Array.isArray(x.visits)) {
          return x.visits.length === 0;
        }
        return true; // If x.visits is not an array (e.g., undefined, null, or something else), treat as without visits
      })
      .map((x) => x.id!) || [];

  // get mother ids to fetch visits and their answers
  const mothersWithoutVisits = mothers
    .filter((mother) => {
      // Has EDD → still pregnant / relevant
      const hasEDD =
        mother.expectedDateOfDelivery !== null &&
        mother.expectedDateOfDelivery !== undefined;

      // No meaningful visits
      const hasNoVisits =
        !Array.isArray(mother.visits) || // null / undefined / object / string / number / …
        mother.visits.length === 0;

      return hasEDD && hasNoVisits;
    })
    .map((x) => x.id!);

  const [infantsListItems, setInfantsListItems] = useState<
    UserAlertListDataItem<ExtraInfantData>[]
  >([]);
  const [mothersListItems, setMothersListItems] = useState<
    UserAlertListDataItem<ExtraMotherData>[]
  >([]);

  const [search, setSearch] = useState('');
  const [searchTextActive, setSearchTextActive] = useState(false);

  const [clientType, setClientType] = useState<SearchDropDownOption<string>[]>(
    []
  );
  const [age, setAge] = useState<SearchDropDownOption<string>[]>([]);
  const [sortBy, setSortBy] = useState<SearchDropDownOption<SortBy>[]>([]);

  const filteredList = useMemo(() => {
    // Tour always shows dummy clients so QA is independent of real caseload.
    if (state.tourActive) {
      let list = [...walkthroughListItems];

      if (clientType[0]?.value === clientTypeOptions[0].value) {
        list = list.filter(
          (item) =>
            (item.extraData as { kind?: string } | undefined)?.kind === 'mother'
        );
      } else if (clientType[0]?.value === clientTypeOptions[1].value) {
        list = list.filter(
          (item) =>
            (item.extraData as { kind?: string } | undefined)?.kind === 'infant'
        );
      }

      if (search.trim()) {
        const q = search.toLowerCase();
        list = list.filter((item) =>
          String(item.title ?? '')
            .toLowerCase()
            .includes(q)
        );
      }

      return list;
    }

    let list = [...mothersListItems, ...infantsListItems];

    list = filterByClientType(
      list,
      mothersListItems,
      infantsListItems,
      clientType
    );
    list = filterByAge(list, age);
    list = filterBySort(list, sortBy);
    list = searchList(list, search);

    return list;
  }, [
    age,
    clientType,
    infantsListItems,
    mothersListItems,
    search,
    sortBy,
    state.tourActive,
    walkthroughListItems,
  ]);

  const isEmptyState = useMemo(
    () =>
      !state.tourActive &&
      (!infants || infants.length === 0) &&
      (!mothers || mothers.length === 0),
    [infants, mothers, state.tourActive]
  );

  useLayoutEffect(() => {
    const infantsList: UserAlertListDataItem<ExtraInfantData>[] = infants.map(
      (infant) => {
        const { years, months } = intervalToDuration({
          start: new Date(infant?.user?.dateOfBirth || ''),
          end: new Date(),
        });

        const infantCaregiver = infant.caregiver || infant.mother?.user;
        return {
          svgIcon: InfantIcon,
          title: `${infant?.firstName ?? infant?.user?.firstName!}  ${
            '& ' + infantCaregiver?.firstName
          }`,
          subTitle: infant?.statusInfo?.subject || 'No visit',
          switchTextStyles: true,
          alertSeverity:
            (infant.statusInfo?.color?.toLocaleLowerCase() as AlertSeverityType) ||
            'none',
          alertSeverityNoneIcon:
            infant?.statusInfo?.subject === 'New client'
              ? 'CheckCircleIcon'
              : 'CalendarIcon',
          alertSeverityNoneColor:
            infant?.statusInfo?.subject === 'New client'
              ? 'successDark'
              : 'black',
          avatarColor: getAvatarColor('growgreat') || '',
          extraData: {
            ...infant,
            under6Months: !years && (!months || months < 6),
            age: `${years}.${months}`,
          },
          onActionClick: async () => {
            const caregiverId = infant.caregiver?.id;
            if (!caregiverId) {
              profileDialog({ client: infant, clientType: 'infant' });
              return;
            }

            const infantsOfThisCaregiver =
              infantsByCaregiver[caregiverId] || [];
            const hasMultiple = infantsOfThisCaregiver.length > 1;

            if (hasMultiple) {
              const path = generatePath(
                ROUTES.CLIENTS.INFANT_PROFILE.MULTIPLE_CHILDREN,
                {
                  infantId: infant.id ?? infant.user?.id,
                  caregiverId: caregiverId,
                }
              );
              history.push(path);
            } else {
              profileDialog({ client: infant, clientType: 'infant' });
            }
          },
        };
      }
    );

    setInfantsListItems(infantsList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, infants]);

  useLayoutEffect(() => {
    const mothersList: UserAlertListDataItem<ExtraMotherData>[] = mothers.map(
      (mother) => {
        return {
          svgIcon: PregnantIcon,
          title:
            mother?.user?.firstName! + ' ' + mother?.user?.surname! ||
            mother?.firstName!,
          subTitle: mother.statusInfo?.subject,
          switchTextStyles: true,
          alertSeverity:
            (mother.statusInfo?.color?.toLocaleLowerCase() as AlertSeverityType) ||
            'none',
          alertSeverityNoneIcon:
            mother?.statusInfo?.subject === 'New client'
              ? 'CheckCircleIcon'
              : 'CalendarIcon',
          alertSeverityNoneColor:
            mother?.statusInfo?.subject === 'New client'
              ? 'successDark'
              : 'black',
          avatarColor: getAvatarColor('growgreat') || '',
          extraData: {
            ...mother,
            under6Months: false,
          },
          onActionClick: () =>
            profileDialog({ client: mother, clientType: 'mother' }),
        };
      }
    );

    setMothersListItems(mothersList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, mothers]);

  const showCompleteProfileBlockingDialog = () => {
    dialog({
      blocking: false,
      position: DialogPosition.Middle,
      render: (onSubmit, onCancel) => {
        return (
          <ActionModal
            className="z-50"
            title="Open a new folder"
            actionButtons={[
              {
                colour: 'primary',
                text: 'Pregnant mom',
                textColour: 'white',
                type: 'filled',
                leadingIcon: 'UserAddIcon',
                onClick: async () => {
                  onSubmit();
                  history.push(ROUTES.MOM_REGISTER);
                },
              },
              {
                colour: 'primary',
                text: 'Child',
                textColour: 'primary',
                type: 'outlined',
                leadingIcon: 'UserGroupIcon',
                onClick: () => {
                  onSubmit();
                  history.push(ROUTES.INFANT_REGISTER);
                },
              },
            ]}
          />
        );
      },
    });
  };

  const goToClientFolders = () => {
    // Multi tour final interactive step: advance to the closing "great job" step.
    if (isMultiClientsTour && state.stepIndex === 4) {
      advanceWalkthroughStep(5);
      return;
    }

    if (state.tourActive) {
      return;
    }

    showCompleteProfileBlockingDialog();
  };

  const onClientTypeChange = useCallback((value) => {
    setAge([]);
    setClientType(value);
  }, []);

  const onMultiTourClientTypeClick = useCallback(() => {
    // Step 0: open the fake client-type menu by advancing to step 1
    if (state.stepIndex === 0) {
      advanceWalkthroughStep(1);
    }
  }, [advanceWalkthroughStep, state.stepIndex]);

  const onMultiTourPregnantMomClick = useCallback(() => {
    setAge([]);
    setClientType([clientTypeOptions[0]]);
    // Step 1: filter to pregnant moms and show the filtered list step
    advanceWalkthroughStep(2);
  }, [advanceWalkthroughStep]);

  useEffect(() => {
    if (!isOnline) return;

    (async () => {
      const promises: Promise<any>[] = [];

      if (allMothersRaw?.length) {
        setIsLoadingMotherStatusInfo(true);
        promises.push(
          appDispatch(motherThunkActions.getMotherStatusInfoForIds())
            .unwrap()
            .finally(() => setIsLoadingMotherStatusInfo(false))
        );
      } else {
        promises.push(appDispatch(motherThunkActions.getMothers()).unwrap());
      }

      if (infants?.length) {
        setIsLoadingInfantStatusInfo(true);
        promises.push(
          appDispatch(infantThunkActions.getInfantStatusInfoForIds())
            .unwrap()
            .finally(() => setIsLoadingInfantStatusInfo(false))
        );
      } else {
        promises.push(appDispatch(infantThunkActions.getInfants()).unwrap());
      }

      await Promise.all(promises);
    })();
  }, [appDispatch]);

  useEffect(() => {
    if (location.state?.isFindClient && !isEmptyState) {
      setSearchTextActive(true);
    }
  }, [location, isEmptyState]);

  // ensure we have all visits for all clients
  useEffect(() => {
    if (infantsWithoutVisits && infantsWithoutVisits.length > 0) {
      appDispatch(
        infantThunkActions.getAllVisitsForInfantIds(infantsWithoutVisits)
      ).unwrap();
    }
  }, []);

  useEffect(() => {
    if (mothersWithoutVisits && mothersWithoutVisits.length > 0) {
      appDispatch(
        motherThunkActions.getAllVisitsForMotherIds(mothersWithoutVisits)
      ).unwrap();
    }
  }, []);

  // During a walkthrough start we must keep tour targets in the DOM.
  // Don't replace the whole list with a spinner if we already have clients
  // (status-info refresh can still run in the background).
  const showBlockingLoader =
    !state.tourActive &&
    (isLoadingMothers ||
      isLoadingInfants ||
      isLoadingMotherStatusInfo ||
      isLoadingInfantStatusInfo);

  if (showBlockingLoader) {
    return (
      <LoadingSpinner
        size="medium"
        spinnerColor={'primary'}
        backgroundColor={'uiLight'}
        className="pt-4"
      />
    );
  }

  return (
    <div className={styles.overlay}>
      {/*
        Walkthrough search step targets this wrapper (not the tiny search icon).
        That way placement:"bottom" sits under the whole filter row, and floater
        has room below instead of flipping above the icon.
      */}
      <div id={getStringFromClassNameOrId(multipleClientsSteps[3].target)}>
        <SearchHeader<UserAlertListDataItem>
          searchItems={filteredList}
          onSearchChange={setSearch}
          isTextSearchActive={searchTextActive}
          onBack={() => setSearchTextActive(false)}
          onSearchButtonClick={() => setSearchTextActive(true)}
          className="flex gap-2 overflow-auto"
        >
          <div
            id={getStringFromClassNameOrId(multipleClientsSteps[0].target)}
            style={{
              // Room for the step-0 tooltip above the Client type filter
              marginTop:
                isMultiClientsTour && state.run && state.stepIndex === 0
                  ? 220
                  : 0,
            }}
          >
            {isMultiClientsTour ? (
              <button
                onClick={onMultiTourClientTypeClick}
                className=" active:bg-secondary border-uiBg right-0 mt-1 flex origin-top-right flex-row items-center justify-between truncate rounded-md border-2 bg-white py-2 pl-4 pr-1 leading-5"
                type="button"
                aria-haspopup="menu"
                aria-expanded={state.stepIndex === 1}
              >
                <p className=" font-h1 text-textLight text-sm font-normal ">
                  Client type
                </p>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                  className="text-primary ml-1 mr-2 h-6 w-6"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </button>
            ) : (
              <SearchDropDown<string>
                displayMenuOverlay={true}
                menuItemClassName={'w-11/12 left-4 '}
                overlayTopOffset={'120'}
                options={clientTypeOptions}
                selectedOptions={clientType}
                onChange={onClientTypeChange}
                placeholder={'Client type'}
                color={'secondary'}
                info={{
                  name: `Filter by: Client type`,
                }}
              />
            )}
          </div>
          {clientType[0]?.value !== clientTypeOptions[0].value && (
            <SearchDropDown<string>
              displayMenuOverlay={true}
              menuItemClassName={'w-11/12 left-4 '}
              overlayTopOffset={'120'}
              options={ageOptions}
              selectedOptions={age}
              onChange={setAge}
              placeholder={'Age'}
              color={'secondary'}
              info={{
                name: `Filter by: Age`,
              }}
            />
          )}
          <SearchDropDown<SortBy>
            displayMenuOverlay={true}
            menuItemClassName={'w-11/12 left-4 '}
            overlayTopOffset={'120'}
            options={sortOptions}
            selectedOptions={sortBy}
            onChange={setSortBy}
            placeholder={'Sort by'}
            color={'secondary'}
            info={{
              name: `Sort by:`,
            }}
          />
        </SearchHeader>
      </div>

      {isMultiClientsTour && state.stepIndex === 1 && (
        <div>
          <p className="border-color-uiLight block border border-t-0 border-r-0 border-l-0 border-dashed pt-4">
            Filter by: Client type
          </p>
          <div
            className="flex p-2"
            id={getStringFromClassNameOrId(multipleClientsSteps[1].target)}
            onClick={onMultiTourPregnantMomClick}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                onMultiTourPregnantMomClick();
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
              className="text-uiLight ml-1 mr-2 h-6 w-6"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              ></path>
            </svg>
            Pregnant mom
          </div>
          <div className="flex p-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
              className="text-uiLight ml-1 mr-2 h-6 w-6"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              ></path>
            </svg>
            Child
          </div>
        </div>
      )}
      <div className={styles.content}>
        {!filteredList.length && (
          <IconInformationIndicator
            className="px-10 pt-28"
            title={
              isEmptyState ? "You don't have any clients yet!" : 'No results'
            }
            subTitle={
              isEmptyState
                ? 'Tap the “Open a folder” button below to register clients'
                : ''
            }
            renderCustomIcon={<BinocularsIcon />}
          />
        )}
        {filteredList.length > 0 && (
          <div id={getStringFromClassNameOrId(clientSteps[1].target)}>
            <StackedList
              className={styles.stackedList}
              listItems={filteredList || []}
              type={'UserAlertList'}
            />
          </div>
        )}
        <FADButton
          id={getStringFromClassNameOrId(multipleClientsSteps[4].target)}
          title={'Open a folder'}
          icon={'PlusIcon'}
          iconDirection={'left'}
          textToggle
          type={'filled'}
          color={'primary'}
          shape={'round'}
          className={styles.fadButton}
          click={goToClientFolders}
        />
      </div>
    </div>
  );
};
